import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import ImageUpload from '../components/ImageUpload';
import '../components/ImageUpload.css';

const EditEvent = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        cost: '',
        category: 'kids',
        eventUrl: '',
        imageUrl: '',
        date: '',
        endDate: '',
        isMultiDay: false
    });

    const categories = [
        { value: 'kids', label: 'Kids Event' },
        { value: 'adults', label: 'Adult Event' }
    ];

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const res = await axios.get(`${config.API_URL}/events/${id}`);
                const event = res.data;
                // Format date to YYYY-MM-DD for input
                const dateObj = new Date(event.date);
                const formattedDate = dateObj.toISOString().split('T')[0];

                let formattedEndDate = '';
                let isMultiDay = false;
                if (event.endDate) {
                    const endDateObj = new Date(event.endDate);
                    formattedEndDate = endDateObj.toISOString().split('T')[0];
                    isMultiDay = true;
                }

                setFormData({
                    title: event.title,
                    description: event.description,
                    cost: event.cost,
                    category: event.category,
                    eventUrl: event.eventUrl,
                    imageUrl: event.imageUrl,
                    date: formattedDate,
                    endDate: formattedEndDate,
                    isMultiDay: isMultiDay
                });
                setLoading(false);
            } catch (err) {
                console.error("Error fetching event", err);
                setError("Failed to load event");
                setLoading(false);
            }
        };

        if (id) fetchEvent();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('token');
            const eventData = {
                ...formData,
                cost: parseFloat(formData.cost)
            };

            await axios.put(`${config.API_URL}/events/${id}`, eventData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setSuccess('Event updated successfully!');

            // Redirect to events after 2 seconds
            setTimeout(() => navigate('/events'), 2000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update event');
        }
    };

    if (loading) return <div className="container">Loading...</div>;

    return (
        <div className="container" style={{ paddingTop: '2rem' }}>
            <div className="form-container">
                <h1>Edit Event</h1>
                {error && <div className="error">{error}</div>}
                {success && <div className="success">{success}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Event Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                            required
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Cost (KSh)</label>
                            <input
                                type="number"
                                name="cost"
                                value={formData.cost}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Event Type</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', marginBottom: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.isMultiDay}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            isMultiDay: e.target.checked,
                                            endDate: e.target.checked ? prev.endDate : ''
                                        }))}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <span>Multi-day event</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{formData.isMultiDay ? 'Start Date' : 'Event Date'}</label>
                            <input
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        {formData.isMultiDay && (
                            <div className="form-group">
                                <label>End Date</label>
                                <input
                                    type="date"
                                    name="endDate"
                                    value={formData.endDate}
                                    onChange={handleChange}
                                    min={formData.date}
                                    required
                                />
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Category</label>
                        <select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                        >
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Event Registration URL</label>
                        <input
                            type="url"
                            name="eventUrl"
                            value={formData.eventUrl}
                            onChange={handleChange}
                            placeholder="https://example.com/register"
                            required
                        />
                    </div>

                    <ImageUpload
                        label="Event Image"
                        value={formData.imageUrl}
                        onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
                    />

                    <button type="submit" className="btn">Update Event</button>
                </form>
            </div>
        </div>
    );
};

export default EditEvent;
